import React, { useEffect, useMemo, useState, useCallback } from "react";
import Select, { SingleValue } from "react-select";
import WebmasterSidebar from "../../components/WebmasterSidebar";
import Swal from "sweetalert2";
import API_BASE_URL from "../../config/apiConfig";
import { useAuth } from "../../context/AuthProvider";

/* ═══════════════════════════════════════════════════════════════════
   TIPURI
   ═══════════════════════════════════════════════════════════════════ */
type Therapist = {
  user_id: string;
  firstname: string;
  lastname: string;
  is_active: boolean;
};
type TherapistOption = { value: string; label: string };

type Block = {
  weekday: number;
  start: string;
  end: string;
  is_active: boolean;
  is_norma: boolean;
};
type ServerRow = {
  id: string;
  weekday: number;
  start: string;
  end: string;
  is_active: boolean;
  is_norma: boolean;
};

type Vacation = {
  id: string;
  therapist_id: string;
  start_date: string;
  end_date: string;
  reason: string | null;
  notes: string | null;
  is_active: boolean;
};

/* ═══════════════════════════════════════════════════════════════════
   CONSTANTE
   ═══════════════════════════════════════════════════════════════════ */
const DAY_LABELS = ["Luni", "Marți", "Miercuri", "Joi", "Vineri", "Sâmbătă", "Duminică"];

const REASON_OPTIONS = ["Concediu odihnă", "Zi liberă"];

type TabKey = "schedule" | "vacations";

const TABS: { key: TabKey; label: string; icon: string }[] = [
  { key: "schedule", label: "Program lucru", icon: "📅" },
  { key: "vacations", label: "Concedii", icon: "🏖️" },
];

// Anii disponibili pt filtru (2025 → anul curent)
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS: number[] = [];
for (let y = CURRENT_YEAR; y >= 2025; y--) YEAR_OPTIONS.push(y);

/* ═══════════════════════════════════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════════════════════════════════ */
const showToast = (icon: "success" | "error", title: string) => {
  Swal.fire({
    toast: true,
    position: "top-end",
    icon,
    title,
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
  });
};

const formatDate = (d: string) =>
  new Date(d).toLocaleDateString("ro-RO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const countDays = (start: string, end: string) => {
  const s = new Date(start);
  const e = new Date(end);
  return Math.round((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24)) + 1;
};

/* ═══════════════════════════════════════════════════════════════════
   COMPONENTA
   ═══════════════════════════════════════════════════════════════════ */
const TherapistWorkSchedule: React.FC = () => {
  const { token } = useAuth();

  const [isCollapsed, setIsCollapsed] = useState(
    () => localStorage.getItem("sidebarCollapsed") === "true"
  );
  const [activeTab, setActiveTab] = useState<TabKey>("schedule");

  // ── Terapeut ──
  const [therapists, setTherapists] = useState<Therapist[]>([]);
  const [selectedTherapist, setSelectedTherapist] = useState<TherapistOption | null>(null);
  const therapistOptions: TherapistOption[] = useMemo(
    () => therapists.map((t) => ({ value: t.user_id, label: `${t.lastname} ${t.firstname}` })),
    [therapists]
  );
  const therapistId = selectedTherapist?.value || "";

  // ── Program lucru ──
  const [blocks, setBlocks] = useState<Block[]>([]);

  // ── Concedii ──
  const [vacations, setVacations] = useState<Vacation[]>([]);
  const [vacStartDate, setVacStartDate] = useState("");
  const [vacEndDate, setVacEndDate] = useState("");
  const [vacReason, setVacReason] = useState(REASON_OPTIONS[0]);
  const [vacNotes, setVacNotes] = useState("");
  const [editingVacId, setEditingVacId] = useState<string | null>(null);
  const [filterYear, setFilterYear] = useState<number>(CURRENT_YEAR);

  // Concedii filtrate pe an
  const filteredVacations = useMemo(() => {
    return vacations.filter((v) => {
      const startYear = new Date(v.start_date).getFullYear();
      const endYear = new Date(v.end_date).getFullYear();
      return startYear === filterYear || endYear === filterYear;
    });
  }, [vacations, filterYear]);

  // Total zile concediu pe anul filtrat
  const totalVacDays = useMemo(() => {
    return filteredVacations.reduce((sum, v) => sum + countDays(v.start_date, v.end_date), 0);
  }, [filteredVacations]);

  /* ─── LOAD TERAPEUȚI ─── */
  useEffect(() => {
    if (!token) return;
    const load = async () => {
      const r = await fetch(`${API_BASE_URL}/users/therapists`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      const active = (data || []).filter((t: Therapist) => t.is_active);
      setTherapists(active);
      if (active.length) {
        setSelectedTherapist({
          value: active[0].user_id,
          label: `${active[0].lastname} ${active[0].firstname}`,
        });
      }
    };
    load();
  }, [token]);

  /* ─── LOAD PROGRAM ─── */
  const loadSchedule = useCallback(
    async (tid: string) => {
      const r = await fetch(`${API_BASE_URL}/admin/work-schedule/${tid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      const rows: ServerRow[] = data?.data || [];
      setBlocks(
        rows.map((x) => ({
          weekday: x.weekday,
          start: x.start,
          end: x.end,
          is_active: x.is_active,
          is_norma: x.is_norma ?? true,
        }))
      );
    },
    [token]
  );

  /* ─── LOAD CONCEDII ─── */
  const loadVacations = useCallback(
    async (tid: string) => {
      const r = await fetch(`${API_BASE_URL}/admin/vacations?therapist_id=${tid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      setVacations(data || []);
    },
    [token]
  );

  useEffect(() => {
    if (!therapistId) return;
    loadSchedule(therapistId);
    loadVacations(therapistId);
    resetVacForm();
  }, [therapistId, loadSchedule, loadVacations]);

  /* ─── PROGRAM LUCRU – acțiuni ─── */
  const addBlock = (weekday: number) => {
    setBlocks((prev) => [
      ...prev,
      { weekday, start: "08:00", end: "14:00", is_active: true, is_norma: true },
    ]);
  };
  const removeBlock = (idx: number) => setBlocks((prev) => prev.filter((_, i) => i !== idx));
  const updateBlock = (idx: number, patch: Partial<Block>) => {
    setBlocks((prev) => prev.map((b, i) => (i === idx ? { ...b, ...patch } : b)));
  };

  const saveSchedule = async () => {
    if (!therapistId) return;
    const r = await fetch(`${API_BASE_URL}/admin/work-schedule/${therapistId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ blocks }),
    });
    if (!r.ok) {
      const { detail } = await r.json().catch(() => ({}));
      showToast("error", detail || "Eroare la salvare.");
      return;
    }
    showToast("success", "Program salvat cu succes.");
  };

  /* ─── CONCEDII – acțiuni ─── */
  const resetVacForm = () => {
    setVacStartDate("");
    setVacEndDate("");
    setVacReason(REASON_OPTIONS[0]);
    setVacNotes("");
    setEditingVacId(null);
  };

  const saveVacation = async () => {
    if (!therapistId || !vacStartDate || !vacEndDate) {
      showToast("error", "Completează datele de start și sfârșit.");
      return;
    }
    if (vacEndDate < vacStartDate) {
      showToast("error", "Data de sfârșit trebuie să fie după data de start.");
      return;
    }
    try {
      if (editingVacId) {
        const r = await fetch(`${API_BASE_URL}/admin/vacations/${editingVacId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            start_date: vacStartDate,
            end_date: vacEndDate,
            reason: vacReason,
            notes: vacNotes || null,
          }),
        });
        if (!r.ok) throw new Error("Eroare la actualizare");
        showToast("success", "Concediu actualizat.");
      } else {
        const r = await fetch(`${API_BASE_URL}/admin/vacations/`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            therapist_id: therapistId,
            start_date: vacStartDate,
            end_date: vacEndDate,
            reason: vacReason,
            notes: vacNotes || null,
          }),
        });
        if (!r.ok) throw new Error("Eroare la creare");
        showToast("success", "Concediu adăugat.");
      }
      resetVacForm();
      loadVacations(therapistId);
    } catch (err: any) {
      showToast("error", err.message || "Eroare.");
    }
  };

  const deleteVacation = async (id: string) => {
    const confirm = await Swal.fire({
      title: "Ștergi concediul?",
      text: "Acțiunea este ireversibilă.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Da, șterge",
      cancelButtonText: "Anulează",
      confirmButtonColor: "#6C298B",
    });
    if (!confirm.isConfirmed) return;
    const r = await fetch(`${API_BASE_URL}/admin/vacations/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.ok) {
      showToast("success", "Concediu șters.");
      loadVacations(therapistId);
    } else {
      showToast("error", "Eroare la ștergere.");
    }
  };

  const editVacation = (v: Vacation) => {
    setEditingVacId(v.id);
    setVacStartDate(v.start_date);
    setVacEndDate(v.end_date);
    setVacReason(v.reason || REASON_OPTIONS[0]);
    setVacNotes(v.notes || "");
    setActiveTab("vacations");
  };

  /* ─── RENDER ─── */
  return (
    <div className={`dashboard-container ${isCollapsed ? "collapsed" : ""}`}>
      <WebmasterSidebar onCollapseChange={setIsCollapsed} />
      <div className="page-content">
        <h1 className="page-title">Setări → Program Terapeuți</h1>

        {/* ── Selector terapeut ── */}
        <div className="form-row therapist-selector">
          <label style={{ minWidth: 90 }}>Terapeut:</label>
          <Select
            options={therapistOptions}
            value={selectedTherapist}
            onChange={(opt: SingleValue<TherapistOption>) => setSelectedTherapist(opt || null)}
            isSearchable
            placeholder="Caută terapeut..."
            styles={{
              container: (base) => ({ ...base, width: 340 }),
              menu: (base) => ({ ...base, zIndex: 20 }),
            }}
            noOptionsMessage={() => "Niciun rezultat"}
          />
        </div>

        {/* ── Tabs ── */}
        {therapistId && (
          <>
            <div className="ws-tabs">
              {TABS.map((tab) => {
                const isActive = activeTab === tab.key;
                const vacCount = tab.key === "vacations" ? filteredVacations.length : 0;
                return (
                  <button
                    key={tab.key}
                    className={`ws-tab ${isActive ? "ws-tab-active" : ""}`}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    <span className="ws-tab-icon">{tab.icon}</span>
                    <span className="ws-tab-label">{tab.label}</span>
                    {vacCount > 0 && (
                      <span className="ws-tab-count">{vacCount}</span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* ══════ TAB: Program lucru ══════ */}
            {activeTab === "schedule" && (
              <>
                <div className="work-grid">
                  {DAY_LABELS.map((label, dayIdx) => (
                    <div key={dayIdx} className="work-day">
                      <div className="work-day-head">
                        <h3>{label}</h3>
                        <button className="primary-btn small" onClick={() => addBlock(dayIdx)}>
                          Adaugă interval
                        </button>
                      </div>
                      <div className="work-day-body">
                        {blocks
                          .map((b, i) => ({ ...b, _idx: i }))
                          .filter((b) => b.weekday === dayIdx)
                          .map((b) => (
                            <div key={b._idx} className="work-block">
                              <label>Start</label>
                              <input type="time" value={b.start} onChange={(e) => updateBlock(b._idx, { start: e.target.value })} />
                              <label>Final</label>
                              <input type="time" value={b.end} onChange={(e) => updateBlock(b._idx, { end: e.target.value })} />
                              <label className="chk">
                                <input type="checkbox" checked={b.is_active} onChange={(e) => updateBlock(b._idx, { is_active: e.target.checked })} />
                                Activ
                              </label>
                              <label className="chk">
                                <input type="checkbox" checked={b.is_norma} onChange={(e) => updateBlock(b._idx, { is_norma: e.target.checked })} />
                                Normă
                              </label>
                              <button className="danger-link" onClick={() => removeBlock(b._idx)}>Șterge</button>
                            </div>
                          ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="form-actions save-schedule">
                  <button className="primary-btn" onClick={saveSchedule}>Salvează program</button>
                </div>
              </>
            )}

            {/* ══════ TAB: Concedii ══════ */}
            {activeTab === "vacations" && (
              <div className="vacation-manager">

                {/* ── Formular ── */}
                <div className="vac-form">
                  <div className="vac-form-grid">
                    <div className="vac-field">
                      <label>De la</label>
                      <input type="date" value={vacStartDate} onChange={(e) => setVacStartDate(e.target.value)} />
                    </div>
                    <div className="vac-field">
                      <label>Până la</label>
                      <input type="date" value={vacEndDate} onChange={(e) => setVacEndDate(e.target.value)} />
                    </div>
                    <div className="vac-field">
                      <label>Tip</label>
                      <select value={vacReason} onChange={(e) => setVacReason(e.target.value)}>
                        {REASON_OPTIONS.map((r) => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    </div>
                    <div className="vac-field vac-field-wide">
                      <label>Note (opțional)</label>
                      <input type="text" value={vacNotes} onChange={(e) => setVacNotes(e.target.value)} placeholder="Detalii..." />
                    </div>
                  </div>
                  <div className="vac-form-actions">
                    <button className="primary-btn" onClick={saveVacation}>
                      {editingVacId ? "Actualizează" : "Adaugă concediu"}
                    </button>
                    {editingVacId && (
                      <button className="secondary-btn" onClick={resetVacForm}>Anulează</button>
                    )}
                  </div>
                </div>

                {/* ── Filtru an + sumar ── */}
                <div className="vac-filter-bar">
                  <div className="vac-year-filter">
                    <label>An:</label>
                    <div className="vac-year-pills">
                      {YEAR_OPTIONS.map((y) => (
                        <button
                          key={y}
                          className={`vac-year-pill ${filterYear === y ? "vac-year-active" : ""}`}
                          onClick={() => setFilterYear(y)}
                        >
                          {y}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="vac-summary">
                    <span className="vac-summary-count">{filteredVacations.length}</span>
                    {filteredVacations.length === 1 ? " concediu" : " concedii"}
                    {" · "}
                    <span className="vac-summary-days">{totalVacDays}</span>
                    {totalVacDays === 1 ? " zi" : " zile"}
                  </div>
                </div>

                {/* ── Tabel ── */}
                <div className="vac-table-wrap">
                  {filteredVacations.length === 0 ? (
                    <div className="vac-empty">
                      Nu există concedii înregistrate în {filterYear}.
                    </div>
                  ) : (
                    <table className="vac-table">
                      <thead>
                        <tr>
                          <th>Perioadă</th>
                          <th>Zile</th>
                          <th>Tip</th>
                          <th>Note</th>
                          <th style={{ width: 140 }}>Acțiuni</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredVacations.map((v) => {
                          const days = countDays(v.start_date, v.end_date);
                          return (
                            <tr key={v.id}>
                              <td>{formatDate(v.start_date)} – {formatDate(v.end_date)}</td>
                              <td><span className="vac-days-badge">{days}</span></td>
                              <td>
                                <span className={`vac-type-tag ${v.reason === "Zi liberă" ? "vac-tag-free" : "vac-tag-rest"}`}>
                                  {v.reason || "—"}
                                </span>
                              </td>
                              <td className="vac-notes-cell">{v.notes || "—"}</td>
                              <td>
                                <button className="vac-action-btn vac-edit" onClick={() => editVacation(v)}>Editează</button>
                                <button className="vac-action-btn vac-delete" onClick={() => deleteVacation(v.id)}>Șterge</button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TherapistWorkSchedule;